import { ICON__link } from '@/assets/icons_fa';
import { use_dispatch } from '@/hooks/_base/use_dispatch';
import { use_effect } from '@/hooks/_base/use_effect';
import { use_slice_generation, use_slice_ui } from '@/hooks/store/use_slices';
import { LOCAL__ui_generation_graph_set_hovered_field } from '@/state/ui/generation/LOCAL__ui_generation_graph_set_hovered_field';
import { UTILS__ml_asset } from '@/utils/UTILS__ml_asset';
import { assert_false } from '@shared/asserts/utils_asserts';
import { DTO__base } from '@shared/common/DTO__base';
import { CONSTANTS__shared } from '@shared/constants/CONSTANTS__shared';
import { ENUM__field_kind } from '@shared/dto/generation/graph_invoke/_enums/ENUM__field_kind';
import { FIELD__base } from '@shared/dto/generation/graph_invoke/_fields/_base/FIELD__base';
import { FIELD__list_image_asset } from '@shared/dto/generation/graph_invoke/_fields/_primitives/list/FIELD__list_image_asset';
import { FIELD_PRIMITIVE__string_hash_ml_asset } from '@shared/dto/generation/graph_invoke/_fields/_primitives/string/hash/FIELD_PRIMITIVE__string_hash_ml_asset';
import { FIELD__image_asset } from '@shared/dto/generation/graph_invoke/_fields/image/FIELD__image_asset';
import { useMemo, useState } from 'react';
import { G_LABEL__hash } from '../G_LABEL__hash';
import { G_GRAPH__handle_left } from '../handles/G_GRAPH__handle_left';
import { G_GRAPH__handle_right } from '../handles/G_GRAPH__handle_right';
import { HOOK__handle_is_connectable_base } from '../handles/HOOK__handle_is_connectable_base';
import { G_GRAPH__field_path } from './G_GRAPH__field_path';
import { G_GRAPH_FIELD__buttons_input } from './G_GRAPH_FIELD__buttons_input';
import { G_VIEW__graph_field_errors } from './G_VIEW__graph_field_errors';
import { G_GRAPH__FIELD__image_asset } from './image/image_asset/G_GRAPH__FIELD__image_asset';
import { G_GRAPH__FIELD__list_image_asset } from './image/list_image_asset/G_GRAPH__FIELD__list_image_asset';

interface PROPS {
  field: FIELD__base;
  field_parent: DTO__base;
  is_list_item?: boolean;
}

export function G_GRAPH_FIELD__row({ field: field_input, field_parent, is_list_item = false }: PROPS) {
  /*
   * STORE
   */
  const dispatch = use_dispatch();

  /*
   * STATE
   */
  const { generation_current } = use_slice_generation(['generation_current']);
  const { g_graph__field_hovered, g_graph__new_edge_field } = use_slice_ui(['g_graph__field_hovered', 'g_graph__new_edge_field']);

  const [is_hovered, set_is_hovered] = useState(false);

  const [field, field_source] = useMemo(() => get_field_from_graph(), [field_input, generation_current.tool.graph]);

  let is_connectable = HOOK__handle_is_connectable_base(field);

  let has_effect_on_generation = generation_current.tool.graph.node_and_field__has_effect_on_generation_runtime(field);

  /*
   * EFFECTS
   */

  use_effect(() => {
    if (is_hovered) dispatch(LOCAL__ui_generation_graph_set_hovered_field(field.meta));

    return () => try_reset_hovered_field();
  }, [is_hovered]);

  /*
   * METHODS
   */

  function get_field_from_graph() {
    let { graph } = generation_current.tool;

    let field = graph.field__get_by_id_or_stub(field_input.meta.field_id);

    let field_source = field.find_source(graph);

    return [field, field_source];
  }

  function try_reset_hovered_field() {
    if (g_graph__field_hovered.field_id != field.meta.field_id) return;

    dispatch(LOCAL__ui_generation_graph_set_hovered_field());
  }

  function get_ml_asset_name(hash_sha256: string) {
    let ml_asset = UTILS__ml_asset.get_by_hash(hash_sha256);

    if (ml_asset.is_stub()) return hash_sha256;

    return `${ml_asset.name} ${ml_asset.version}`;
  }

  function try_get_ml_asset_value() {
    let { graph } = generation_current.tool;

    let out = field_source.field_connectable__value(graph);

    if (field instanceof FIELD_PRIMITIVE__string_hash_ml_asset) {
      out = get_ml_asset_name(field.get_value());
    }

    return out;
  }

  function render_value() {
    assert_false(field.is_stub());

    if (field.meta.data__is_linked) return render_value_linked();

    if (is_list_item) return '';

    let out = try_get_ml_asset_value();

    return out;
  }

  function get_class_name() {
    let out = '';

    if (!g_graph__new_edge_field.is_stub()) {
      out = is_connectable ? '!opacity-100' : '!opacity-10';
    } else if (g_graph__field_hovered.node_id == field.meta.node_id) {
      out = get_class_name_by_hovering();
    } else {
      out = 'opacity-100';
    }

    return out;
  }
  function get_class_name_by_hovering() {
    assert_false(g_graph__field_hovered.is_stub());

    return g_graph__field_hovered.field_id == field.meta.field_id ? 'opacity-100' : 'opacity-50';
  }

  /*
   * METHODS
   */
  function need_render_handle_left_error() {
    if (!g_graph__new_edge_field.is_stub()) return false;
    if (field.meta.field_id == g_graph__field_hovered.field_id) return false; //hide for hovered field

    return field.meta.errors.is_empty() ? false : true;
  }

  /*
   * RENDERS
   */

  function render_value_linked() {
    return (
      <div className="flex items-center gap-1 opacity-30">
        <ICON__link className="!size-3 min-w-3 min-h-3" />
        linked
      </div>
    );
  }

  function render_image_preview() {
    let out = null;

    if (field.meta.data__is_linked) {
      //do nothing
    } else if (field_source instanceof FIELD__list_image_asset) {
      out = <G_GRAPH__FIELD__list_image_asset {...{ field: field_source }} />;
    } else if (field_source instanceof FIELD__image_asset) {
      out = <G_GRAPH__FIELD__image_asset {...{ field: field_source }} />;
    }

    return out;
  }

  /*
   * HTML
   */
  if (field.is_stub()) return null;
  if (generation_current.tool.graph.meta.graph_id != field.meta.graph_id) return null;
  if (field_parent.is_stub()) return null; //parent not initialized

  return (
    <>
      <div className="relative flex flex-row items-center text-left">
        <div
          className={`relative flex items-center gap-2 w-full ${get_class_name()}`}
          style={{ height: `${CONSTANTS__shared.GRID_CELL_SIZE}px` }}
          {...{ onMouseEnter: () => set_is_hovered(true), onMouseLeave: () => set_is_hovered(false) }}
        >
          {field.meta.field_kind == ENUM__field_kind.INPUT && <G_GRAPH__handle_left {...{ field }} />}

          <div className="w-0" />

          <G_GRAPH__field_path {...{ field }} />

          <G_LABEL__hash hash={field.meta.field_hash__value_full} {...{ has_effect_on_generation }} />
          <G_LABEL__hash hash={field.meta.field_hash__value_core} {...{ has_effect_on_generation }} />

          <span className="flex flex-row items-center w-24 min-w-24 max-w-24 text-xs text-gray-500 truncate">{render_value()}</span>
          <span className="block w-20 min-w-24 max-w-24 text-xs text-gray-500 truncate">{field.field_connectable__type()}</span>
          <div className="w-0" />

          <G_GRAPH__handle_right {...{ field }} />

          {field.meta.field_kind == ENUM__field_kind.INPUT && <G_GRAPH_FIELD__buttons_input {...{ field, is_list_item }} />}
        </div>
        {need_render_handle_left_error() && <G_VIEW__graph_field_errors errors={field.meta.errors} />}
      </div>
      {render_image_preview()}
    </>
  );
}
